import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Clock, XCircle, CalendarX, Inbox } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";

const STATUS_STYLES = {
  pending: "bg-accent/20 text-accent-foreground border-accent/40",
  confirmed: "bg-primary/10 text-primary border-primary/30",
  completed: "bg-secondary text-secondary-foreground border-border",
  cancelled: "bg-destructive/10 text-destructive border-destructive/30",
};

function prettyDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
  });
}

export default function MyBookings() {
  const [email, setEmail] = useState("");
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await api.get("/bookings/lookup", { params: { email: email.trim() } });
      setBookings(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`, { email: email.trim() });
      toast.success("Booking cancelled");
      search();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">Manage your visit</span>
        <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">Find my bookings</h1>
        <p className="text-muted-foreground mt-2">Enter the email you used to book. You can review or cancel upcoming visits.</p>

        <form onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="lookup-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1"
            required
          />
          <button
            data-testid="lookup-search-button"
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold px-7 py-3 rounded transition-transform active:scale-95 hover:bg-primary/90 disabled:opacity-50"
          >
            <Search className="w-4 h-4" /> {loading ? "Searching..." : "Search"}
          </button>
        </form>

        {bookings !== null && (
          <div className="mt-10 space-y-4" data-testid="lookup-results">
            {bookings.length === 0 ? (
              <div className="bg-card border border-border rounded p-12 text-center" data-testid="lookup-empty">
                <Inbox className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No bookings found for that email.</p>
              </div>
            ) : (
              bookings.map((b, i) => {
                const canCancel = b.status === "pending" || b.status === "confirmed";
                return (
                  <motion.div
                    key={b.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    data-testid={`lookup-booking-${b.id}`}
                    className="bg-card border border-border rounded p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-display font-bold text-lg">{b.service_name}</h3>
                        <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded border capitalize ${STATUS_STYLES[b.status]}`}>
                          {b.status}
                        </span>
                      </div>
                      <div className="text-sm text-primary font-semibold mt-1.5 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> {prettyDate(b.date)} · {b.time}
                      </div>
                      {b.special_request && (
                        <p className="text-xs text-muted-foreground mt-2 italic">"{b.special_request}"</p>
                      )}
                    </div>
                    {canCancel ? (
                      <button
                        data-testid={`cancel-booking-${b.id}`}
                        onClick={() => cancel(b.id)}
                        className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded border border-border text-destructive hover:border-destructive transition-colors shrink-0"
                      >
                        <XCircle className="w-4 h-4" /> Cancel
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-sm text-muted-foreground shrink-0">
                        <CalendarX className="w-4 h-4" /> Not cancellable
                      </span>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
